import { isNetworkError } from '@/lib/isNetworkError';
import { logError } from '@/lib/logError';
import rest from '@/lib/rest';
import { RestError } from '@dabble/rest-client';
import { resetMarketing } from '@/lib/marketing';
import { useUserStore } from '@/stores/user';
import { defineStore } from 'pinia';
import posthog from 'posthog-js';
import { computed, readonly, ref } from 'vue';

export interface AuthAccount {
  uid: string;
  name?: string;
  email?: string;
  photoUrl?: string;
  admin?: boolean;
  lastSeen?: number;
}

/**
 * Holds the list of accounts the browser is currently signed in to (the
 * sealed `dbl2` cookie maintained by dabble-rest), and the currently-active
 * uid. Mirrors the pattern in dabble-writer-3.0's accounts store, stripped of
 * the hub/spoke worker layer.
 *
 * Active-uid resolution priority on init:
 *   1. sessionStorage.impersonate  (admin acting as a user)
 *   2. sessionStorage.uid
 *   3. localStorage.uid
 *   4. first account from GET /auth/accounts
 */
export const useAccountsStore = defineStore('accounts', () => {
  const accounts = ref<AuthAccount[]>([]);
  const currentUid = ref<string>('');
  const isImpersonating = ref(false);
  const userDataReady = ref(false);
  let switchInFlight: Promise<void> | null = null;
  let initPromise: Promise<void> | null = null;

  const otherAccounts = computed(() => accounts.value.filter((a) => a.uid !== currentUid.value));

  function init(): Promise<void> {
    if (initPromise) return initPromise;
    initPromise = (async () => {
      try {
        migrateLegacyToken();
        const userStore = useUserStore();

        const impersonate = sessionStorage.getItem('impersonate') || '';
        const uid =
          impersonate ||
          sessionStorage.getItem('uid') ||
          localStorage.getItem('uid') ||
          (await loadDefaultUidFromRest());

        if (!uid) return;

        isImpersonating.value = !!impersonate;
        currentUid.value = uid;
        sessionStorage.setItem('uid', uid);
        // Impersonation is session-only — never write the impersonated uid to
        // localStorage, otherwise a tab close would leave the admin "stuck" as
        // the target user on next visit.
        if (!impersonate) localStorage.setItem('uid', uid);

        try {
          await userStore.fetchUser(uid);
        } catch (err) {
          // A 401 means this uid has no valid session — the `dbl2` cookie has
          // expired/been cleared, or the user signed out elsewhere — even though
          // a stale uid marker survived in storage. That's an expected
          // unauthenticated state during bootstrap, not a reportable error
          // (DABBLE-ACCOUNTS-2-6). Drop the stale markers so the app settles into
          // a clean logged-out state and we don't re-fire the same 401 on every
          // reload. Mirrors the sibling loadAccounts() 401 swallow (#92).
          //
          // A 403 "Not permitted to act as another user" is the same class of
          // stale state seen from the *impersonation* angle (DABBLE-ACCOUNTS-2-G):
          // the session itself is valid, but the resolved uid — a stale
          // `sessionStorage.impersonate`, or a uid no longer among the dbl2
          // cookie's accounts — is one this session is not allowed to act as, so
          // the `x-uid` on GET /auth/user is rejected. Like the 401, it's an
          // expected stale-marker bootstrap condition, not a reportable error:
          // drop the markers so the next load re-derives a valid account from the
          // cookie instead of re-firing the same 403 on every reload. We match the
          // server message so a genuinely different 403 is still surfaced.
          //
          // A 404 USER_NOT_FOUND is the third flavour of the same stale-marker
          // condition (DABBLE-ACCOUNTS-2-6): the resolved uid — a storage marker or
          // a cross-env `?uid=` handoff — points at a user that no longer exists
          // (deleted account). Like the 401/403 it's an expected bootstrap outcome,
          // and clearing the markers is what stops the same 404 re-firing on every
          // single page load. We match the exact server body so an unrelated 404 is
          // still surfaced. Any other failure is still reported.
          if (
            err instanceof RestError &&
            (err.code === 401 ||
              (err.code === 403 && /not permitted to act as another user/i.test(err.message)) ||
              (err.code === 404 && err.message === 'USER_NOT_FOUND'))
          ) {
            clearActiveUid();
            return;
          }
          // A 503 is a transient upstream identity-service blip (DABBLE-ACCOUNTS-2-7),
          // not a stale marker: the session is probably fine, so deliberately do NOT
          // clearActiveUid() — a momentary outage must never sign the user out. The
          // next load retries the same uid. console.warn leaves a Sentry breadcrumb
          // without creating an event (no captureConsoleIntegration — see main.ts).
          if (err instanceof RestError && err.code === 503) {
            console.warn('Skipping user load, service unavailable:', err);
            return;
          }
          // A transient browser-level fetch failure — the request never got an
          // HTTP response at all (offline, connection dropped, or the tab
          // navigating away mid-bootstrap). Per the fetch spec that rejects with
          // a TypeError whose wording is browser-specific: "Failed to fetch"
          // (Chrome/Edge/Opera), "NetworkError when attempting to fetch
          // resource." (Firefox), "Load failed" (Safari). Not a first-party bug
          // and not actionable, so don't report it (DABBLE-ACCOUNTS-2-1N).
          // isNetworkError deliberately excludes RestError, so a real HTTP
          // failure — including an unexpected 403 or a 5xx — still reports.
          if (!isNetworkError(err)) logError('Failed to load user:', err);
        }

        // Best-effort populate the list for the Switch Account dropdown.
        loadAccounts().catch(() => {});
      } finally {
        userDataReady.value = true;
      }
    })();
    return initPromise;
  }

  async function loadAccounts() {
    try {
      const list = (await rest.get('/auth/accounts').credentials('include').send()) as AuthAccount[] | null;
      accounts.value = list ?? [];
      return accounts.value;
    } catch (err) {
      // A 401 here is expected when the browser has no valid session yet (e.g. on
      // the login page before sign-in, or after the session cookie expires).
      // loadAccounts is best-effort — see the `loadAccounts().catch(() => {})`
      // call site — so swallow expected auth failures instead of reporting them
      // to Sentry as errors (DABBLE-ACCOUNTS-2-B). Mirrors loadDefaultUidFromRest,
      // which already treats the same call's failure as benign.
      //
      // A 503 is likewise expected-in-the-wild: a transient upstream identity-service
      // outage (DABBLE-ACCOUNTS-2-7). The dropdown list is best-effort and recovers on
      // the next load, so warn (breadcrumb only) rather than report an error.
      if (err instanceof RestError && err.code === 503) {
        console.warn('Failed to load accounts (service unavailable):', err);
      } else if (!(err instanceof RestError && err.code === 401) && !isNetworkError(err)) {
        // A transient browser-level fetch failure (offline, dropped connection,
        // request cancelled by navigating away) is swallowed for the same reason —
        // it never received an HTTP response, so there's no first-party bug to act
        // on (DABBLE-ACCOUNTS-2-11 Firefox / -2-17 Safari). isNetworkError matches
        // only the browser-specific TypeErrors and never a RestError, so genuine
        // server failures (5xx, unexpected 403s) still report.
        logError('Failed to load accounts:', err);
      }
      return [];
    }
  }

  async function loadDefaultUidFromRest(): Promise<string> {
    try {
      const list = (await rest.get('/auth/accounts').credentials('include').send()) as AuthAccount[] | null;
      return list?.[0]?.uid || '';
    } catch {
      return '';
    }
  }

  /**
   * Drop every marker that points at an active account, returning the store to
   * a clean logged-out state. Used when bootstrap discovers a stale uid whose
   * session is no longer valid (a 401 from `/auth/user`). Mirrors the cleanup
   * performed by the sign-out / switch flows.
   */
  function clearActiveUid() {
    currentUid.value = '';
    isImpersonating.value = false;
    sessionStorage.removeItem('impersonate');
    sessionStorage.removeItem('uid');
    localStorage.removeItem('uid');
  }

  /**
   * Settle the store into a clean logged-out state after a live request reveals
   * the active session has expired — e.g. a 401 from `/auth/user` issued when an
   * account view mounts, long after the cached router guard authorised the
   * navigation. Drops the active-uid markers and clears the loaded user,
   * mirroring the local cleanup in `auth.logout()` minus the server round-trip
   * (the session is already dead, so calling it would just 401 again).
   * Navigation back to login is the caller's responsibility.
   */
  function handleExpiredSession() {
    clearActiveUid();
    useUserStore().clear();
    // Reset PostHog identity, exactly as logout()/switchAccount()/stopImpersonating()
    // do. Those paths reload the page (which also drops the distinct_id); this one
    // redirects via an in-app router push, so without an explicit reset the expired
    // user's distinct_id would linger — mis-attributing login-page events to them
    // and risking an identity merge if a different user signs in next. Reset the
    // ad-tracking identity too — this path does NOT reload, so GA's user_id/email
    // would otherwise linger and mis-attribute later events.
    posthog.reset();
    resetMarketing();
  }

  /**
   * Switch the active account to another already-signed-in user.
   *
   * Purely client-side: write the new uid to `sessionStorage.uid` (this tab) and
   * `localStorage.uid` (default for new tabs) and reload. Init re-derives the
   * active uid from those markers, and requests carry it as `x-uid`. The shared
   * `dbl2` cookie is never reordered, so a second tab keeps its own account.
   */
  async function switchAccount(uid: string) {
    if (!uid || uid === currentUid.value) return;
    // Re-entrancy guard — set synchronously before any await.
    if (switchInFlight) return switchInFlight;

    switchInFlight = (async () => {
      sessionStorage.removeItem('impersonate');
      sessionStorage.setItem('uid', uid);
      localStorage.setItem('uid', uid);
      posthog.reset();
      resetMarketing();
      // `/billing/portal`, not `/` — the root now hands authenticated users to
      // the writer app, which would eject you from accounts mid-switch.
      location.assign('/billing/portal');
    })();

    try {
      await switchInFlight;
    } finally {
      switchInFlight = null;
    }
  }

  /**
   * init() set this tab's uid to the impersonated user, so drop it too and let
   * the reload fall back to `localStorage.uid`.
   */
  function endImpersonation() {
    sessionStorage.removeItem('impersonate');
    sessionStorage.removeItem('uid');
    posthog.reset();
    resetMarketing();
  }

  function stopImpersonating() {
    endImpersonation();
    // `/billing/portal`, not `/` — see switchAccount above.
    location.assign('/billing/portal');
  }

  /**
   * Stop impersonating and bounce back to the admin app on the matching
   * environment (local / test / prod), derived by swapping the `accounts.`
   * subdomain for `admin.` in the current hostname.
   */
  function stopImpersonatingToAdmin() {
    endImpersonation();
    const adminHost = location.hostname.replace(/^accounts\./, 'admin.');
    location.assign(`${location.protocol}//${adminHost}/`);
  }

  /**
   * One-time migration from the legacy `userToken` JSON envelope to the
   * plain `localStorage.uid` scheme. Runs at the top of init() so existing
   * users stay signed in across the upgrade.
   */
  function migrateLegacyToken() {
    if (localStorage.getItem('uid')) return;
    const legacy = localStorage.getItem('userToken');
    if (!legacy) return;
    try {
      const parsed = JSON.parse(legacy);
      if (parsed?.value && (!parsed.expirey || Date.parse(parsed.expirey) > Date.now())) {
        localStorage.setItem('uid', parsed.value);
      }
    } catch {}
    localStorage.removeItem('userToken');
  }

  return {
    accounts: readonly(accounts),
    otherAccounts,
    currentUid: readonly(currentUid),
    isImpersonating: readonly(isImpersonating),
    userDataReady: readonly(userDataReady),
    init,
    loadAccounts,
    switchAccount,
    stopImpersonating,
    stopImpersonatingToAdmin,
    handleExpiredSession,
  };
});
